import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MessageCircle, Loader2, FileText, Wallet, ShieldCheck, Wrench, Copy, Pencil } from "lucide-react";
import { toast } from "sonner";

const KIND = {
  budget: { label: "Orçamento sem resposta", icon: FileText, cls: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
  payment: { label: "Pagamento", icon: Wallet, cls: "bg-red-500/10 text-red-700 border-red-500/30" },
  warranty: { label: "Garantia vencendo", icon: ShieldCheck, cls: "bg-sky-500/10 text-sky-700 border-sky-500/30" },
  maintenance: { label: "Manutenção", icon: Wrench, cls: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" },
};

export const FollowUps = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [edit, setEdit] = useState(null);
  const [text, setText] = useState("");
  useEffect(() => { api.get("/followups").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  if (!items) return <div className="h-40 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const msgOf = (it) => it.message.replace("{link}", it.share_token ? `${window.location.origin}/doc/${it.share_token}` : "");
  const send = (it, custom) => {
    const wa = (it.client_whatsapp || "").replace(/\D/g, ""); const n = wa && wa.length <= 11 ? `55${wa}` : wa;
    const m = custom ?? msgOf(it);
    if (!n) { navigator.clipboard.writeText(m); toast.success("Cliente sem WhatsApp — mensagem copiada"); return; }
    window.open(`https://wa.me/${n}?text=${encodeURIComponent(m)}`, "_blank");
  };
  const counts = Object.keys(KIND).reduce((a, k) => ({ ...a, [k]: items.filter((i) => i.kind === k).length }), {});
  const shown = items.filter((i) => filter === "all" || i.kind === filter);

  return (
    <div className="space-y-3" data-testid="followups">
      <div className="grid grid-cols-4 gap-1.5">
        {Object.entries(KIND).map(([k, v]) => (
          <button key={k} onClick={() => setFilter(filter === k ? "all" : k)} data-testid={`followup-filter-${k}`} className={`rounded-xl border p-2 text-left transition-colors ${filter === k ? "border-primary ring-1 ring-primary" : ""} ${v.cls}`}>
            <p className="text-xl font-display font-extrabold leading-none">{counts[k]}</p><p className="text-[10px] font-medium leading-tight mt-1">{v.label}</p>
          </button>
        ))}
      </div>
      {shown.length === 0 ? <Card className="p-6 text-center text-sm text-muted-foreground">Nenhum follow-up pendente. Tudo em dia! 🎉</Card> : shown.map((it) => {
        const K = KIND[it.kind];
        return (
          <Card key={it.id} className="p-3 space-y-2" data-testid={`followup-${it.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2 min-w-0">
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 border ${K.cls}`}><K.icon className="h-4 w-4" /></div>
                <div className="min-w-0"><p className="font-semibold text-sm truncate">{it.title} • {it.client_name}</p><p className="text-xs text-muted-foreground">{it.detail}</p></div>
              </div>
              {it.doc_id ? <Link to={`/documents/${it.doc_id}`}><Badge variant="outline" className="text-[10px] shrink-0">{it.number}</Badge></Link> : it.equipment_id ? <Link to={`/equipments/${it.equipment_id}`}><Badge variant="outline" className="text-[10px] shrink-0">Equipamento</Badge></Link> : null}
            </div>
            <div className="grid grid-cols-[1fr_auto_auto] gap-2">
              <Button size="sm" className="h-10 bg-[#25D366] hover:bg-[#1da851] text-white" onClick={() => send(it)} data-testid={`followup-send-${it.id}`}><MessageCircle className="h-4 w-4 mr-1" /> Enviar WhatsApp</Button>
              <Button size="sm" variant="outline" className="h-10 w-10 p-0" onClick={() => { setEdit(it); setText(msgOf(it)); }} data-testid={`followup-edit-${it.id}`} aria-label="Editar mensagem"><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="h-10 w-10 p-0" onClick={() => { navigator.clipboard.writeText(msgOf(it)); toast.success("Mensagem copiada"); }} data-testid={`followup-copy-${it.id}`} aria-label="Copiar"><Copy className="h-4 w-4" /></Button>
            </div>
          </Card>
        );
      })}
      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar mensagem</DialogTitle></DialogHeader>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} className="min-h-[160px]" data-testid="followup-message" />
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancelar</Button><Button className="bg-[#25D366] hover:bg-[#1da851] text-white" onClick={() => { send(edit, text); setEdit(null); }} data-testid="followup-send-edited"><MessageCircle className="h-4 w-4 mr-1" /> Enviar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
